/**
 * Editing a brief's name and schedule on the Briefs page, without Zoe. What it
 * gathers, and an alert's condition and message, still come only from Zoe.
 *
 * An edit replaces the whole definition (PUT /briefs/:id), so everything the
 * form does not touch goes back exactly as it was loaded, under the revision
 * shown: a brief changed since is a 409, never silently overwritten. The
 * server validates the result, including whether `on_data` can apply.
 */
import type { BriefDefinition, BriefInput, BriefSchedule, Frequency } from '../types/briefs';

/** The brief as loaded, ready to send back with some fields changed. */
export function inputOf(b: BriefDefinition, change: Partial<BriefInput> = {}): BriefInput {
  const { name, plan, template, schedule, enabled, condition, condition_label, message } = b;
  return {
    name, plan, template, schedule, enabled,
    ...(condition ? { condition } : {}),
    ...(condition_label ? { condition_label } : {}),
    ...(message ? { message } : {}),
    ...change,
  };
}

export type RepeatChoice = 0 | 15 | 30 | 60;

/** Every field the form shows, whatever the frequency; `scheduleFromForm` keeps only those that apply. */
export interface ScheduleForm {
  frequency: Frequency;
  day_of_week: number;
  local_time: string;
  /** Alerts only; 0 = once at `local_time`. */
  every_minutes: RepeatChoice;
  /** With `every_minutes`; '' = until the end of the day. */
  until: string;
}

/** What a clock schedule starts from when the brief had none (an on_data alert moving to a schedule). */
const DEFAULT_TIME = '07:00';

export function formFromSchedule(s: BriefSchedule): ScheduleForm {
  return {
    frequency: s.frequency,
    day_of_week: s.day_of_week ?? 1,
    local_time: s.local_time ?? DEFAULT_TIME,
    every_minutes: s.every_minutes ?? 0,
    until: s.until ?? '',
  };
}

/** The schedule to save. The time zone is the brief's own; the form doesn't change it. */
export function scheduleFromForm(f: ScheduleForm, timezone: string, isAlert: boolean): BriefSchedule {
  if (f.frequency === 'on_data') return { frequency: 'on_data', timezone };
  const repeat = isAlert && f.every_minutes ? f.every_minutes : undefined;
  return {
    frequency: f.frequency,
    ...(f.frequency === 'weekly' ? { day_of_week: f.day_of_week } : {}),
    local_time: f.local_time,
    timezone,
    ...(repeat ? { every_minutes: repeat } : {}),
    ...(repeat && f.until ? { until: f.until } : {}),
  };
}
